import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useWelcome } from './WelcomeContext';
import {
  FEEDBACK_PROMPT_DELAY_MS,
  isForceFeedbackPrompt,
  openFeedbackForm,
  readFeedbackDismissed,
  writeFeedbackDismissed,
} from '../lib/feedback';

// Two pieces of state, lifted to the app shell so the persistent feedback
// pill and the auto-prompt toast can coordinate (the pill hides while the
// prompt is on screen). The form itself opens in a new tab — there's no
// in-app modal because Google Forms refuses to render in an iframe when
// the form has any sign-in or domain restriction.
interface FeedbackContextValue {
  // True once the auto-prompt timer has fired AND the user hasn't yet
  // dismissed/opened it this session.
  promptVisible: boolean;
  // Open the form in a new tab. Used by both the persistent pill and
  // the prompt's "Share feedback" CTA. Suppresses the auto-prompt if it
  // was on screen.
  openSurvey: () => void;
  // Dismiss the auto-prompt without opening the form ("Maybe later").
  // Persists in localStorage so the prompt doesn't re-fire on refresh
  // or on subsequent navigation between Explorer ↔ Detail views.
  dismissPrompt: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { open: welcomeOpen } = useWelcome();
  const [promptVisible, setPromptVisible] = useState(false);
  // Captured once at mount so a later localStorage write (when the user
  // dismisses) doesn't re-evaluate the gate and re-fire the timer.
  // `?feedback=1` overrides the dismissed flag for booth resets.
  const [dismissed, setDismissed] = useState<boolean>(
    () => !isForceFeedbackPrompt() && readFeedbackDismissed(),
  );

  // Schedule the auto-prompt. Only starts the timer once the welcome
  // splash has closed — otherwise the prompt could fire while the user
  // is still reading the splash, and the splash's z=2050 backdrop would
  // hide the prompt anyway. On return visits (Welcome already seen),
  // `welcomeOpen` is already `false` on first render so the timer
  // starts immediately.
  useEffect(() => {
    if (dismissed) return;
    if (welcomeOpen) return;
    const id = window.setTimeout(() => {
      setPromptVisible(true);
    }, FEEDBACK_PROMPT_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [welcomeOpen, dismissed]);

  const dismissPrompt = useCallback(() => {
    writeFeedbackDismissed();
    setDismissed(true);
    setPromptVisible(false);
  }, []);

  const openSurvey = useCallback(() => {
    openFeedbackForm();
    // Hide the auto-prompt (if it was on screen) but DON'T mark dismissed
    // — the user might bail before submitting and we want the persistent
    // pill to remain available for re-engagement.
    setPromptVisible(false);
  }, []);

  const value = useMemo<FeedbackContextValue>(
    () => ({ promptVisible, openSurvey, dismissPrompt }),
    [promptVisible, openSurvey, dismissPrompt],
  );

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};

export const useFeedback = (): FeedbackContextValue => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return ctx;
};
